import SizeUtility from '../../../js/Utilities/SizeUtility.js';

class SchoolMap extends HTMLElement {
  activeTabIndex = false;
  lastActiveTabIndex = 0;
  maxTabIndex = 0;
  mode = null;
  namespace = null;
  tabsDropdown = null;
  tabsSelectGroup = null;
  tabsHolder = null;
  tabList = null;
  tabs = [];
  tabPanelList = null;
  tabPanels = [];
  tabCloseButtons = [];

  mapAPIKey = '';
  mapLoaded = false;
  mapBoundaryFunction = null;
  map = null;
  google = null;
  mapCenter = { lat: 37.7749, lng: -122.4194 };

  filters = [];
  filterForm = null;
  filterFormClear = null;
  filterTypes = {};

  emptyResultsMessage = null;

  constructor() {
    super();

    this.namespace = this.id || 'school-map';
    this.setAttribute('data-ready', true);

    this.mapAPIKey = this.getAttribute('data-map-api-key') || '';

    this.tabsHolder = this.querySelector('.school-map__nav');
    this.tabList = this.querySelector('.school-map__list');
    this.tabs = [...this.querySelectorAll('.school-map__button')];
    this.tabPanelList = this.querySelector('.school-map__panels');
    this.tabPanels = [...this.querySelectorAll('.school-map__panel')];
    this.tabCloseButtons = [...this.querySelectorAll('.school-map__close')];

    this.filters = [...this.querySelectorAll('.school-map__nav input')];
    this.filterForm = this.querySelector('.school-map__nav form');
    this.filterFormClear = this.querySelector('.school-map__clear');

    this.emptyResultsMessage = this.querySelector('.school-map__empty');

    // If we don't have any tabs, bail
    if (!this.tabs.length) {
      return;
    }

    // If more tabs than panels, remove excess tabs from the DOM
    if (this.tabs.length > this.tabPanels.length) {
      for (let i = this.tabPanels.length; i < this.tabs.length; i++) {
        this.tabs[i].parentNode.removeChild(this.tabs[i]);
      }
      this.tabs = this.tabs.slice(0, this.tabPanels.length);
    }

    // If more panels than tabs, remove excess panels from the DOM
    if (this.tabPanels.length > this.tabs.length) {
      for (let i = this.tabs.length; i < this.tabPanels.length; i++) {
        this.tabPanels[i].parentNode.removeChild(this.tabPanels[i]);
      }
      this.tabPanels = this.tabPanels.slice(0, this.tabs.length);
    }

    this.maxTabIndex = this.tabs.length - 1;

    this.setupMap();
    this.setupTabs();
    this.setupFilters();
  }

  connectedCallback() {
    this.onWindowResize();

    this.addEventListeners();
  }

  addEventListeners() {
    window.addEventListener('resize', () => this.onWindowResize());
  }

  removeEventListeners() {
    window.removeEventListener('resize', () => this.onWindowResize());
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  setupMap() {
    if (this.mapLoaded || this.mode == 'mobile') return;

    if (this.getAttribute('data-lat'))
      this.mapCenter.lat = parseFloat(this.getAttribute('data-lat'));
    if (this.getAttribute('data-lng'))
      this.mapCenter.lng = parseFloat(this.getAttribute('data-lng'));

    // Add Google Maps API Library to page
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${this.mapAPIKey}&callback=initMap`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    // Create Google Map
    window.initMap = async () => {
      await this.completeMapLoad();
    };

    this.mapLoaded = true;
  }

  async completeMapLoad() {
    const { Map } = await google.maps.importLibrary('maps');
    const { AdvancedMarkerElement } = await google.maps.importLibrary('marker');
    this.google = google;

    this.map = new Map(this.querySelector('.school-map__map'), {
      center: this.mapCenter,
      zoom: 10,
      mapId: this.namespace,
    });

    // Add markers to map
    this.tabs.forEach(tab => {
      let marker = new AdvancedMarkerElement({
        position: {
          lat: parseFloat(tab.getAttribute('data-lat')),
          lng: parseFloat(tab.getAttribute('data-lng')),
        },
        map: this.map,
        content: tab.querySelector('.school-map__marker').cloneNode(true),
      });

      // Add click event listener to marker
      marker.addListener('click', () => {
        tab.click();
      });

      tab.marker = marker;
    });

    this.centerMap();
  }

  centerMap() {
    if (!this.google) return;

    var bounds = new this.google.maps.LatLngBounds();
    let boundsSet = false;

    this.tabs.forEach((tab, i) => {
      if (this.activeTabIndex !== false && i !== this.activeTabIndex) return;
      if (tab.getAttribute('hidden')) return;
      bounds.extend(tab.marker.position);
      boundsSet = true;
    });

    if (boundsSet) {
      this.map.fitBounds(bounds);

      if (this.map.getZoom() > 15) {
        this.map.setZoom(15);
      }
    } else {
      this.map.setCenter(this.mapCenter);
      this.map.setZoom(6);
    }

    //console.log('centerMap');
  }

  setupTabs() {
    this.tabsHolder.removeAttribute('hidden');

    this.tabs.forEach((tab, i) => {
      tab.id = `${this.namespace}-tab-${i}`;

      tab.addEventListener('click', () => {
        this.onTabClick(i);
      });
      tab.addEventListener('keydown', e => this.onTabKeyDown(i, e));
    });

    if (this.tabPanelList) {
      this.tabPanelList.classList.remove('flow');
    }

    this.tabPanels.forEach((panel, i) => {
      panel.id = `${this.namespace}-tabpanel-${i}`;
      panel.setAttribute('aria-labelledby', this.tabs[i].id);

      panel.addEventListener('keydown', e => this.onPanelKeyDown(i, e));
    });

    this.tabs.forEach((tab, i) => {
      tab.setAttribute('aria-controls', this.tabPanels[i].id);
    });

    this.tabCloseButtons.forEach((button, i) => {
      button.addEventListener('click', () => {
        this.onCloseClick();
      });
    });

    this.onWindowResize();
    this.updateTabs();
  }

  setupFilters() {
    this.filterTypes = {};
    this.filters.forEach(filter => {
      if (filter.type == 'search') {
        this.filterTypes[filter.name] = {
          type: 'search',
          name: filter.name,
          value: '',
          element: filter,
        };
        filter.addEventListener('input', e => {
          this.updateFilters();
        });
      } else if (filter.type == 'checkbox') {
        if (!this.filterTypes[filter.name]) {
          this.filterTypes[filter.name] = {
            type: 'checkbox',
            name: filter.name.replace('[]', ''),
            value: [],
            element: [],
          };
        }
        this.filterTypes[filter.name].element.push(filter);
        filter.addEventListener('change', e => {
          this.updateFilters();
        });
      }
    });

    this.filterForm.addEventListener('submit', e => {
      e.preventDefault();
      this.updateFilters();
    });

    this.filterFormClear.addEventListener('click', e => {
      this.updateFilters();
    });

    this.filterForm.querySelector('button[type="submit"]').setAttribute('hidden', true);
  }

  updateFilters() {
    for (const [key, filterType] of Object.entries(this.filterTypes)) {
      if (filterType.type == 'search') {
        filterType.value = filterType.element.value;
      } else if (filterType.type == 'checkbox') {
        filterType.value = [];
        filterType.element.forEach(checkbox => {
          if (checkbox.checked) filterType.value.push(checkbox.value);
        });
      }
      this.filterTypes[key] = filterType;
    }

    let resultsCount = 0;

    this.tabs.forEach((tab, i) => {
      let show = true;
      for (const [key, filterType] of Object.entries(this.filterTypes)) {
        if (filterType.type == 'search') {
          if (
            tab.getAttribute('data-' + filterType.name) &&
            !tab
              .getAttribute('data-' + filterType.name)
              .toLowerCase()
              .includes(filterType.value.toLowerCase())
          ) {
            show = false;
          }
        } else if (filterType.type == 'checkbox') {
          if (
            filterType.value.length > 0 &&
            !filterType.value.includes(tab.getAttribute('data-' + filterType.name))
          ) {
            show = false;
          }
        }
      }

      if (show) {
        tab.removeAttribute('hidden');
        resultsCount++;
      } else {
        tab.setAttribute('hidden', true);

        if (i === this.activeTabIndex) {
          this.activeTabIndex = false;
          this.onCloseClick();
        }
      }
    });

    if (resultsCount === 0) {
      this.emptyResultsMessage.removeAttribute('hidden');
      this.tabList.setAttribute('hidden', true);
    } else {
      this.emptyResultsMessage.setAttribute('hidden', true);
      this.tabList.removeAttribute('hidden');
    }

    this.updateTabs();
  }

  updateTabs() {
    if (this.activeTabIndex === false) {
      if (this.tabPanelList) this.tabPanelList.setAttribute('hidden', true);
    } else {
      if (this.tabPanelList) this.tabPanelList.removeAttribute('hidden');

      if (this.activeTabIndex < 0) {
        this.activeTabIndex = this.maxTabIndex;
      }

      if (this.activeTabIndex > this.maxTabIndex) {
        this.activeTabIndex = 0;
      }
    }

    this.tabs.forEach((tab, i) => {
      tab.setAttribute('aria-selected', i === this.activeTabIndex);
      tab.setAttribute(
        'tabindex',
        i === this.activeTabIndex ||
          (this.activeTabIndex === false && i === this.lastActiveTabIndex)
          ? 0
          : -1,
      );

      if (tab.marker) {
        if (tab.hasAttribute('hidden')) {
          tab.marker.setMap(null);
        } else {
          tab.marker.setMap(this.map);
        }
      }
    });

    this.tabPanels.forEach((panel, i) => {
      panel.setAttribute('aria-hidden', i !== this.activeTabIndex);
      if (i !== this.activeTabIndex) {
        panel.removeAttribute('tabindex');
      } else {
        panel.setAttribute('tabindex', -1);
      }
    });

    this.setupMap();
    this.centerMap();
  }

  onTabClick(index, isInitial = false) {
    if (this.activeTabIndex !== false) {
      this.lastActiveTabIndex = this.activeTabIndex;
    }
    this.activeTabIndex = index;
    this.updateTabs();

    if (!isInitial) {
      this.tabs[this.activeTabIndex].focus();
    }
  }
  onCloseClick() {
    if (this.activeTabIndex !== false) {
      this.lastActiveTabIndex = this.activeTabIndex;
      this.tabs[this.activeTabIndex].focus();
    }

    this.activeTabIndex = false;
    this.updateTabs();
  }

  checkNextKey(e) {
    return 'ArrowDown' === e.key;
  }

  checkPrevKey(e) {
    return 'ArrowUp' === e.key;
  }

  checkFocusInKey(e) {
    const isFocusInKey = {
      mobile: 'ArrowDown',
      desktop: 'ArrowRight',
    };

    return isFocusInKey[this.mode] === e.key;
  }

  checkFocusOutKey(e) {
    const isFocusOutKey = {
      mobile: 'ArrowUp',
      desktop: 'ArrowLeft',
    };

    return isFocusOutKey[this.mode] === e.key;
  }

  onTabKeyDown(index, e) {
    if (e.key === 'Enter' || e.key === ' ') {
      this.activeTabIndex = index;
      this.updateTabs();

      return;
    }

    if (this.checkNextKey(e)) {
      this.onTabClick(index + 1);
      return;
    }

    if (this.checkPrevKey(e)) {
      this.onTabClick(index - 1);
      return;
    }

    if (this.checkFocusInKey(e)) {
      this.tabPanels[this.activeTabIndex].focus();
    }
  }

  onPanelKeyDown(index, e) {
    if (this.checkFocusOutKey(e)) {
      this.tabs[this.activeTabIndex].focus();
    }
  }

  onWindowResize() {
    this.mode = SizeUtility.isScreenGtMd() ? 'desktop' : 'mobile';

    this.updateTabs();
  }
}

customElements.define('school-map', SchoolMap);
